import { Injectable } from '@angular/core';
import { CanActivate, CanActivateChild, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { ConfigService } from '../../services/config/config.service';
import { AuthService } from '../../services/auth/auth.service';
import { RoleSelectionService } from '../../services/role-selection/role-selection.service';
import { UnauthorizedComponent } from '../../components/unauthorized/unauthorized.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate, CanActivateChild {
  private authorizationRouteMap = {
    'flight-info': 'flightList',
    'flight-list': 'flightList',
    capacity: 'gateCapacity',
    deice: 'deice',
    throughput: 'stationThroughput',
    'advanced-search': 'advancedSearch',
    'policy-engine': 'policyEngine',
    cancellations: 'cancellation',
    'irop-replay': 'iropReplay',
    'jv-gates': 'jvGates',
    'severe-weather-index': 'severeWeatherIndex',
    'air-traffic-control': 'airTrafficControl',
    diversionManagement: 'diversionManagement',
    'operational-codes': 'operationalCodes',
    'user-access': 'userAccess',
    'wit': 'wit'
  };

  constructor(
    private authService: AuthService,
    private router: Router,
    private configService: ConfigService,
    private roleSelectionService: RoleSelectionService,
        private modalService: NgbModal
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> | Promise<boolean> {
    if (this.authService.isValidToken) {
      const selectedRoleParam = sessionStorage.getItem('selectedRole');
        if (route.queryParams['Role']) {
          const vipsRole = route.queryParams['Role'];
          return this.authService.getAuthorization(null, this.formatVipsRole(vipsRole)).then(byPassResult => {
            if (byPassResult && byPassResult.sub) {
              const authorized = this.setAuth(byPassResult, route);
              if (!authorized) {
                // this.modalService.open(UnauthorizedComponent, { centered: true });
                console.log('Not authorized to access the resource.');
                return false;
              }
            } else {
              this.authService.openRoleSelectionModal(route);
            }
            return true;
          }).catch(error => {
            console.log('Authorization failed (vipsRole path), redirecting to login:', error);
            return false;
          });
        }
        else if (selectedRoleParam) {          
          return this.authService.getAuthorization(selectedRoleParam).then(byPassResult => {
            if (byPassResult && byPassResult.sub) {
              const authorized = this.setAuth(byPassResult, route);
              if (!authorized) {
                // this.modalService.open(UnauthorizedComponent, { centered: true });
                console.log('Not authorized to access the resource.');
                return false;
              }
            } else {
              this.authService.openRoleSelectionModal(route);
            }

            return true;
          }).catch(error => {
            console.log('Authorization failed (selectedRole path), redirecting to login:', error);
            return false;
          });
        } else {
          return this.authService.getAuthorization().then(result => {
            if (result && result.sub) {
              const authorized = this.setAuth(result, route);
              if (!authorized) {
                // this.modalService.open(UnauthorizedComponent, { centered: true });
                console.log('Not authorized to access the resource.');
                return false;
              }
            } else {
              this.authService.openRoleSelectionModal(route);
            }
            // TODO: always return true right now because Authentication is mocked in BFF
            return true;
          }).catch(error => {
            // Token is invalid/expired, treat as not logged in
            console.log('Authorization failed, treating as not logged in:', error);
            return false;
          });
        }
    } else {
        // We are not currently authenticated or bypassing auth,
        //  so start that process...

        // Save the location requiring auth that we were trying to access
        // so we can return to it later
        // NOTE: Have to use localStorage instead of sessionStorage since IE11 does
        //       not appear to persist sessionStorage across redirects, so when the
        //       OIDC flow returns to /auth-callback, sessionStorage is empty
        const preURLwithouthost = window.location.href.split(window.location.host)[1];
        const postURLwithouthost = state.url;
        localStorage.setItem(this.configService.getSettings('preAuthPageSessionKey'), preURLwithouthost);
        localStorage.setItem(this.configService.getSettings('postAuthPageSessionKey'), postURLwithouthost);
        localStorage.setItem(this.configService.getSettings('preAuthTypeKey'), AuthService.preAuthTypeKeyFull);

        console.log('User not logged in - beginning auth process...');
        this.authService.startAuthentication();
        return false;
      }
    }

  setAuth(result, route) {
    const authMap = JSON.parse(result.sub);
    const path = route.url && route.url[0] && route.url[0].path ? route.url[0].path : '';
    const authKey = this.authorizationRouteMap[path];
    if (authMap && authKey) {
      const access: boolean = authMap[authKey].access;
      console.log(`Authorized for ${authKey}:`, access);
      return access;
    }
    // If authKey is empty (home screen), allow access
    if (!authKey) {
      return true;
    }
    return false;
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | Observable<boolean> | Promise<boolean> {
    return this.canActivate(route, state);
  }

  private formatVipsRole(vipsRole: string): string {
    let formattedVipsRole = vipsRole;
    if (vipsRole.slice(vipsRole.length - 1) === '$') {
      formattedVipsRole = vipsRole.slice(0, -1)
    }
    return formattedVipsRole;
  }
}
